import {useFeatureFlag} from '@malt/feature-flags';
import type {AdvancedSearchResponse} from '@api';
import {canEnterCheckoutStep, canResumeCheckoutStep, canResumeSelectionStep} from './utils/stepsValidations';

export interface StepConfig {
    routeName: string;
    skipTo?: string;
    canEnter: (advancedSearch: AdvancedSearchResponse) => boolean;
    canResume: (advancedSearch: AdvancedSearchResponse) => boolean;
    // If true, this step will be excluded from navigation history/back stack
    excludeFromHistory?: boolean;
}

/**
 * We used to have two or three workflows for the advanced search funnel.
 * Now we only have one but we kept the workflow system for future flexibility.
 * If the future team judge that it's not needed anymore, we can remove it.
 */
function useAdvancedSearchWorkflows() {
    const advancedSearchCalibrationPage = useFeatureFlag('advanced-search-calibration-page');
    return {
        unified: [
            {
                routeName: 'criteria',
                skipTo: 'checkout',
                canEnter: () => true,
                canResume: () => true,
            },
            ...(advancedSearchCalibrationPage.value
                ? [
                      {
                          routeName: 'calibration',
                          canEnter: () => true,
                          canResume: () => false,
                          skipTo: 'checkout',
                      },
                  ]
                : []),
            {
                routeName: 'selection',
                skipTo: 'checkout',
                canEnter: () => true,
                canResume: (advancedSearch: AdvancedSearchResponse) => canResumeSelectionStep(advancedSearch),
            },
            {
                routeName: 'checkout',
                canEnter: (advancedSearch: AdvancedSearchResponse) => canEnterCheckoutStep(advancedSearch),
                canResume: (advancedSearch: AdvancedSearchResponse) => canResumeCheckoutStep(advancedSearch),
            },
        ],
    };
}

export type AdvancedSearchWorkflows = ReturnType<typeof useAdvancedSearchWorkflows>;
export type WorkflowKey = keyof AdvancedSearchWorkflows;

export type AdvancedSearchStep<W extends WorkflowKey> = AdvancedSearchWorkflows[W][number]['routeName'];

export function useAdvancedSearchWorkflowSteps(workflowKey: WorkflowKey): StepConfig[] {
    const workflows = useAdvancedSearchWorkflows();
    return workflows[workflowKey];
}
